import * as tokenService from "./tokenService";
import { refreshSession } from "./refreshManager";

/**
 * Client-side inactivity tracking (spec §11).
 *
 * SHARED FILE — byte-identical in prx_shell-ui, prx_buyerapp-ui,
 * prx_buyer_WorkOrder_ui and prx_master-ui (canonical: prx_buyerapp-ui).
 *
 * Activity writes go to localStorage (throttled) so idleness is shared across
 * tabs AND across the four SPA bundles: typing in /p2p keeps a /wom tab
 * alive, and Stay Logged In in one tab clears the warning everywhere. No
 * backend call is ever made from an activity event.
 */

const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"] as const;
const ACTIVITY_WRITE_THROTTLE_MS = 5_000;
const CHECK_INTERVAL_MS = 5_000;

// Proactive silent refresh while the user is ACTIVE: renews the access token
// ~2 minutes before expiry so routine work never even sees a 401 burst. The
// interceptor's 401 path remains the safety net if this is turned off.
const PROACTIVE_REFRESH = true;
const PROACTIVE_REFRESH_SKEW_MS = 120_000;

export interface SessionCallbacks {
  /** Warning threshold crossed; `deadline` is the ms-epoch the session idles out. */
  onWarn: (deadline: number) => void;
  /** Activity elsewhere (another tab) cleared the warning. */
  onWarnCleared: () => void;
  onExpire: () => void;
}

let intervalId: number | null = null;
let detachActivity: (() => void) | null = null;
let warningActive = false;
let expiredFired = false;
let lastActivityWrite = 0;

function markActivity(): void {
  const now = Date.now();
  if (now - lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastActivityWrite = now;
  tokenService.setLastActivityAt(now);
}

function check(callbacks: SessionCallbacks): void {
  if (!tokenService.isAuthenticated()) return;

  const now = Date.now();
  const lastActivity = tokenService.getLastActivityAt() ?? now;
  const idleFor = now - lastActivity;
  const idleTimeout = tokenService.getIdleTimeoutMs();
  const warnBefore = tokenService.getWarnBeforeMs();

  if (idleFor >= idleTimeout) {
    if (!expiredFired) {
      expiredFired = true;
      warningActive = false;
      callbacks.onExpire();
    }
    return;
  }
  expiredFired = false;

  if (idleFor >= idleTimeout - warnBefore) {
    if (!warningActive) {
      warningActive = true;
      callbacks.onWarn(lastActivity + idleTimeout);
    }
  } else if (warningActive) {
    warningActive = false;
    callbacks.onWarnCleared();
  }

  if (
    PROACTIVE_REFRESH &&
    !warningActive &&
    tokenService.isAccessTokenExpired(PROACTIVE_REFRESH_SKEW_MS)
  ) {
    void refreshSession();
  }
}

export function startSessionManager(callbacks: SessionCallbacks): void {
  stopSessionManager();
  resetIdle();

  const onVisibility = () => {
    if (document.visibilityState === "visible") check(callbacks);
  };
  ACTIVITY_EVENTS.forEach((eventName) =>
    window.addEventListener(eventName, markActivity, { capture: true, passive: true })
  );
  document.addEventListener("visibilitychange", onVisibility);
  detachActivity = () => {
    ACTIVITY_EVENTS.forEach((eventName) =>
      window.removeEventListener(eventName, markActivity, { capture: true })
    );
    document.removeEventListener("visibilitychange", onVisibility);
  };

  intervalId = window.setInterval(() => check(callbacks), CHECK_INTERVAL_MS);
}

export function stopSessionManager(): void {
  if (intervalId != null) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
  detachActivity?.();
  detachActivity = null;
  warningActive = false;
  expiredFired = false;
}

/** Stay Logged In / login: force the activity clock to now, everywhere. */
export function resetIdle(): void {
  lastActivityWrite = 0;
  markActivity();
}
